import { Link } from "react-router-dom";
import type { Movie } from "@/types/movie";

interface MovieRowProps {
  title: string;
  subtitle?: string;
  moreLink: string;
  movies: Movie[];
  favorites?: string[];
  onToggleFavorite?: (id: string) => void;
}

export default function MovieRow({
  title,
  subtitle,
  moreLink,
  movies,
  favorites = [],
  onToggleFavorite,
}: MovieRowProps) {
  return (
    <section className="w-full px-4 pt-12 md:px-8">
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold text-foreground-950">{title}</h2>
          {subtitle && <p className="mt-2 text-sm text-foreground-600">{subtitle}</p>}
        </div>
        <Link
          to={moreLink}
          className="flex shrink-0 items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-accent-600"
        >
          查看更多 <i className="ri-arrow-right-line"></i>
        </Link>
      </div>

      <div className="no-scrollbar -mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 md:gap-4 md:px-0">
        {movies.map((movie) => {
          const fav = favorites.includes(movie.id);
          return (
            <Link
              key={movie.id}
              to={`/detail/${movie.id}`}
              className="group w-36 shrink-0 snap-start sm:w-40 md:w-44"
            >
              <div className="relative aspect-[2/3] w-full overflow-hidden rounded-lg bg-background-200">
                <img
                  src={movie.cover}
                  alt={`${movie.title} 海报`}
                  title={`${movie.title} 伦理影视`}
                  className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-2 top-2 rounded-md bg-primary-950/80 px-2 py-0.5 text-xs font-semibold text-accent-300">
                  {movie.rating.toFixed(1)}
                </span>
                {onToggleFavorite && (
                  <button
                    type="button"
                    aria-label={fav ? "取消收藏" : "收藏"}
                    onClick={(event) => {
                      event.preventDefault();
                      onToggleFavorite(movie.id);
                    }}
                    className={`absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full border transition-colors ${
                      fav
                        ? "border-accent-400 bg-accent-500 text-primary-950"
                        : "border-background-200/70 bg-background-50/90 text-foreground-600 hover:text-accent-600"
                    }`}
                  >
                    <i className={fav ? "ri-heart-fill text-sm" : "ri-heart-line text-sm"}></i>
                  </button>
                )}
              </div>
              <h3 className="mt-2 truncate font-heading text-sm font-semibold text-foreground-950 group-hover:text-primary-600">
                {movie.title}
              </h3>
              <p className="mt-0.5 text-xs text-foreground-600">
                {movie.year} · {movie.type === "movie" ? "电影" : `${movie.episodes}集`}
              </p>
            </Link>
          );
        })}
      </div>
    </section>
  );
}